import React, { useMemo } from 'react';

import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, IconButton, List, ListItemButton, ListItemText, Tooltip, Typography, alpha } from '@mui/material';

import { ChatThread } from './AiChatContext';
import { getThreadPreview, isEmptyChatThread } from './chatThreadUtils';

interface AiChatConversationListProps {
  threads: Record<string, ChatThread>;
  activeThreadId: string | null;
  onSelect: (threadId: string) => void;
  onDelete: (threadId: string) => void;
  onNewChat: () => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(timestamp: number): number {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function getThreadGroupLabel(timestamp: number): string {
  const today = startOfDay(Date.now());
  const day = startOfDay(timestamp);
  const diffDays = Math.round((today - day) / DAY_MS);

  if (diffDays <= 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays <= 7) {
    return 'Previous 7 days';
  }
  if (diffDays <= 30) {
    return 'Previous 30 days';
  }
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export default function AiChatConversationList({
  threads,
  activeThreadId,
  onSelect,
  onDelete,
  onNewChat,
}: AiChatConversationListProps) {
  const groupedThreads = useMemo(() => {
    const sorted = Object.values(threads)
      .filter((thread) => !isEmptyChatThread(thread))
      .sort((a, b) => b.updatedAt - a.updatedAt);
    const groups: Array<{ label: string; threads: Array<ChatThread> }> = [];
    for (const thread of sorted) {
      const label = getThreadGroupLabel(thread.updatedAt);
      const last = groups[groups.length - 1];
      if (last?.label === label) {
        last.threads.push(thread);
      } else {
        groups.push({ label, threads: [thread] });
      }
    }
    return groups;
  }, [threads]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCommentOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={onNewChat}
          fullWidth
          sx={{
            justifyContent: 'flex-start',
            px: 1.5,
            py: 0.9,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.85rem',
            letterSpacing: 0,
            color: 'text.primary',
            borderColor: (theme) => alpha(theme.palette.divider, 0.7),
            bgcolor: 'background.paper',
            transition: 'all 140ms ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              color: 'primary.main',
            },
          }}
        >
          New chat
        </Button>
      </Box>

      {groupedThreads.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.75 }}>
            No conversations yet. Start a new chat to see history here.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1.25, pb: 1.5 }}>
          {groupedThreads.map((group) => (
            <Box key={group.label} sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pt: 1.25,
                  pb: 0.5,
                  fontWeight: 600,
                  fontSize: '0.66rem',
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  opacity: 0.7,
                }}
              >
                {group.label}
              </Typography>
              <List dense disablePadding>
                {group.threads.map((thread) => {
                  const isActive = thread.id === activeThreadId;
                  return (
                    <ListItemButton
                      key={thread.id}
                      selected={isActive}
                      onClick={() => onSelect(thread.id)}
                      sx={{
                        position: 'relative',
                        py: 0.9,
                        pl: 1.5,
                        pr: 1,
                        borderRadius: 2,
                        mb: 0.25,
                        transition: 'background-color 120ms ease',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
                        },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        },
                        '&.Mui-selected::before': {
                          content: '""',
                          position: 'absolute',
                          left: 4,
                          top: 8,
                          bottom: 8,
                          width: 3,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                        },
                        '& .ai-chat-delete': {
                          opacity: 0,
                          transition: 'opacity 120ms ease-in-out',
                        },
                        '&:hover .ai-chat-delete, &.Mui-selected .ai-chat-delete': {
                          opacity: 1,
                        },
                      }}
                    >
                      <ListItemText
                        primary={thread.title}
                        secondary={getThreadPreview(thread.messages)}
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            fontWeight: isActive ? 600 : 500,
                            noWrap: true,
                            sx: {
                              color: isActive ? 'primary.main' : 'text.primary',
                              lineHeight: 1.3,
                            },
                          },
                          secondary: {
                            variant: 'caption',
                            color: 'text.secondary',
                            noWrap: true,
                            sx: { mt: 0.25, opacity: 0.75 },
                          },
                        }}
                      />
                      <Tooltip title="Delete conversation">
                        <IconButton
                          edge="end"
                          size="small"
                          className="ai-chat-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(thread.id);
                          }}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'error.main',
                              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
